import type { MantineColorsTuple } from "@mantine/core";
import { generateStandardPalette } from "./palette";
import { hexColorWithOpacity } from "./utils";

/** Design scale steps (50 = lightest, 900 = darkest). */
export const COLOR_STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const;

export type ColorStep = (typeof COLOR_STEPS)[number];

const STEP_TO_INDEX: Record<ColorStep, number> = {
  50: 0,
  100: 1,
  200: 2,
  300: 3,
  400: 4,
  500: 5,
  600: 6,
  700: 7,
  800: 8,
  900: 9,
};

/** Canonical ocean (teal) ramp — every step is explicit from Figma / brand. */
export const OCEAN_HEX: Record<ColorStep, string> = {
  50: "#F4F9FA",
  100: "#E3F0F2",
  200: "#D2E7EA",
  300: "#ADD4DC",
  400: "#8DC3CE",
  500: "#0F758A",
  600: "#00596B",
  700: "#003C48",
  800: "#002229",
  900: "#00151A",
};

/** Neutral grey ramp — disabled surfaces, borders, and non-brand chrome. */
export const GRAY_HEX: Record<ColorStep, string> = {
  50: "#F8F9FA",
  100: "#F1F3F5",
  200: "#E9ECEF",
  300: "#DEE2E6",
  400: "#CED4DA",
  500: "#ADB5BD",
  600: "#868E96",
  700: "#495057",
  800: "#343A40",
  900: "#212529",
};

/** Figma gold anchors on the gold ramp. */
export const GOLD_ANCHOR_500 = "#FFD472";
export const GOLD_ANCHOR_100 = "#FFF3D6";

export const CORAL_ANCHOR_400 = "#FFB8A6";
export const PINK_ANCHOR_200 = "#FFD6DD";
export const LAVENDER_ANCHOR_200 = "#D6DAFF";
export const MINT_ANCHOR_400 = "#96FF8A";
/** Soft mint for facilitator home survey Done pill (Figma). */
export const MINT_SOFT_HEX = "#D9F0C6";
export const BLUE_ANCHOR_200 = "#B9DEFF";

export const INK_HEX = "#081C23";
export const BLACK_HEX = "#000000";
export const WHITE_HEX = "#FFFFFF";
export const RED_700_HEX = "#F03E3E";
export const RED_900_HEX = "#C92A2A";
export const INK_OVERLAY_HEX = "#071E24";
export const INK_OVERLAY_ALPHA_80 = hexColorWithOpacity(INK_OVERLAY_HEX, 0.8);

export function colorStepToMantineIndex(step: ColorStep): number {
  return STEP_TO_INDEX[step];
}

export function mantineIndexToColorStep(index: number): ColorStep {
  const step = COLOR_STEPS[index];
  if (step === undefined) {
    throw new RangeError(`Invalid Mantine color index: ${index}`);
  }
  return step;
}

/** Build a full step map from one anchor hex, filling other steps via standard palette math. */
export function buildHueRampFromAnchor(
  anchorStep: ColorStep,
  anchorHex: string,
  explicitOverrides?: Partial<Record<ColorStep, string>>,
): Record<ColorStep, string> {
  const tuple = generateStandardPalette(anchorHex);
  const anchorIdx = STEP_TO_INDEX[anchorStep];
  const ramp = {} as Record<ColorStep, string>;

  for (const step of COLOR_STEPS) {
    const stepIdx = STEP_TO_INDEX[step];
    const tupleIdx = stepIdx - anchorIdx + 5;
    const clamped = Math.max(0, Math.min(9, tupleIdx));
    ramp[step] = tuple[clamped];
  }

  if (explicitOverrides) {
    for (const step of COLOR_STEPS) {
      const hex = explicitOverrides[step];
      if (hex !== undefined) {
        ramp[step] = hex;
      }
    }
  }

  return ramp;
}

export function hueRampToMantineTuple(ramp: Record<ColorStep, string>): MantineColorsTuple {
  return COLOR_STEPS.map((step) => ramp[step]) as unknown as MantineColorsTuple;
}

export const oceanRamp = OCEAN_HEX;
export const grayRamp = GRAY_HEX;
/** Existing generated ocean accent scale, exposed as L0 for L1 migration. */
export const accentRamp = buildHueRampFromAnchor(500, OCEAN_HEX[600]);
export const goldRamp = buildHueRampFromAnchor(500, GOLD_ANCHOR_500, { 100: GOLD_ANCHOR_100 });
export const coralRamp = buildHueRampFromAnchor(400, CORAL_ANCHOR_400);
export const pinkRamp = buildHueRampFromAnchor(200, PINK_ANCHOR_200);
export const lavenderRamp = buildHueRampFromAnchor(200, LAVENDER_ANCHOR_200);
export const mintRamp = buildHueRampFromAnchor(400, MINT_ANCHOR_400);
export const blueRamp = buildHueRampFromAnchor(200, BLUE_ANCHOR_200);

export const oceanMantineTuple = hueRampToMantineTuple(oceanRamp);
export const grayMantineTuple = hueRampToMantineTuple(grayRamp);
export const goldMantineTuple = hueRampToMantineTuple(goldRamp);
export const coralMantineTuple = hueRampToMantineTuple(coralRamp);
export const pinkMantineTuple = hueRampToMantineTuple(pinkRamp);
export const lavenderMantineTuple = hueRampToMantineTuple(lavenderRamp);
export const mintMantineTuple = hueRampToMantineTuple(mintRamp);

/** @deprecated Use goldRamp[500] — kept for call sites migrating off GOLD_BASE_HEX. */
export const GOLD_BASE_HEX = goldRamp[500];
